import { supabase } from '../supabase/config';

/**
 * Maps a snake_case DB row to camelCase sale object.
 */
function toSale(row) {
    if (!row) return null;
    return {
        id: row.id,
        tenantId: row.tenant_id,
        orderId: row.order_id,
        customerId: row.customer_id,
        customerName: row.customer_name,
        amount: row.amount,
        pointsEarned: row.points_earned,
        items: row.items,
        type: row.type,
        timestamp: row.timestamp,
        quantity: row.items ? row.items.reduce((acc, i) => acc + i.quantity, 0) : 1,
    };
}

/**
 * Maps a snake_case DB row to camelCase expense object.
 */
function toExpense(row) {
    if (!row) return null;
    return {
        id: row.id,
        tenantId: row.tenant_id,
        title: row.title,
        category: row.category,
        amount: row.amount,
        date: row.date,
        createdAt: row.created_at,
    };
}

/**
 * Service to handle financial operations, sales tracking, and expense management.
 */
export const accountingService = {
    /**
     * Subscribes to recent sales for a tenant.
     */
    subscribeToSales(tenantId, callback) {
        if (!tenantId) return () => {};

        // Initial fetch
        supabase
            .from('sales')
            .select('*')
            .eq('tenant_id', tenantId)
            .order('timestamp', { ascending: false })
            .limit(2000)
            .then(({ data }) => {
                if (data) callback(data.map(toSale));
            });

        // Real-time subscription
        const channel = supabase
            .channel('sales-' + tenantId)
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'sales', filter: `tenant_id=eq.${tenantId}` },
                () => {
                    supabase
                        .from('sales')
                        .select('*')
                        .eq('tenant_id', tenantId)
                        .order('timestamp', { ascending: false })
                        .limit(2000)
                        .then(({ data }) => {
                            if (data) callback(data.map(toSale));
                        });
                }
            )
            .subscribe();

        return () => supabase.removeChannel(channel);
    },

    /**
     * Subscribes to operational expenses.
     */
    subscribeToExpenses(tenantId, callback) {
        if (!tenantId) return () => {};

        // Initial fetch
        supabase
            .from('accounting_expenses')
            .select('*')
            .eq('tenant_id', tenantId)
            .order('date', { ascending: false })
            .then(({ data }) => {
                if (data) callback(data.map(toExpense));
            });

        // Real-time subscription
        const channel = supabase
            .channel('expenses-' + tenantId)
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'accounting_expenses', filter: `tenant_id=eq.${tenantId}` },
                () => {
                    supabase
                        .from('accounting_expenses')
                        .select('*')
                        .eq('tenant_id', tenantId)
                        .order('date', { ascending: false })
                        .then(({ data }) => {
                            if (data) callback(data.map(toExpense));
                        });
                }
            )
            .subscribe();

        return () => supabase.removeChannel(channel);
    },

    /**
     * Adds a new expense record.
     */
    async addExpense(expenseData, tenantId) {
        const { data, error } = await supabase
            .from('accounting_expenses')
            .insert({
                tenant_id: tenantId,
                title: expenseData.title,
                category: expenseData.category,
                amount: parseFloat(expenseData.amount),
                date: expenseData.date,
                created_at: new Date().toISOString(),
            })
            .select('id')
            .single();

        if (error) throw error;
        return data;
    },

    /**
     * Saves unit costs for products.
     */
    async saveProductCosts(tenantId, costs) {
        const { error } = await supabase
            .from('product_costs')
            .upsert({
                tenant_id: tenantId,
                costs,
                updated_at: new Date().toISOString(),
            }, { onConflict: 'tenant_id' });

        if (error) throw error;
    }
};
